import { mapFarms } from './mapMockData';

const METRIC_DEFS = [
  {
    id: 'temperature',
    label: 'Temperature',
    unit: '°C',
    color: '#8A4A3A',
  },
  {
    id: 'humidity',
    label: 'Air Humidity',
    unit: '%',
    color: '#4A7A8A',
  },
  {
    id: 'wind',
    label: 'Wind Speed',
    unit: 'm/s',
    color: '#6B6B6B',
  },
  {
    id: 'solar',
    label: 'Solar Radiation',
    unit: 'W/m²',
    color: '#B68A2E',
  },
];

const METRIC_READING_KEYS = {
  temperature: 'temperature',
  humidity: 'humidity',
  wind: 'windSpeed',
  solar: 'solarRadiation',
};

const METRIC_FALLBACKS = {
  temperature: { field: 29.4, under: 28.3 },
  humidity: { field: 70.2, under: 76.8 },
  wind: { field: 7.8, under: 5.9 },
  solar: { field: 760, under: 470 },
};

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const avgReading = (stations, readingKey) => {
  const values = (stations || [])
    .map((station) => station?.currentReadings?.[readingKey])
    .filter((value) => Number.isFinite(value));

  if (!values.length) return null;
  return values.reduce((sum, value) => sum + value, 0) / values.length;
};

const deterministicRipple = (index, scale) => {
  const wave = Math.sin((index + 1) * 1.37) + Math.cos((index + 1) * 0.73);
  return wave * scale;
};

const roundMetric = (metricId, value) => {
  if (metricId === 'solar') return Math.round(value);
  return Number(value.toFixed(1));
};

const normalizeByType = (metricId, fieldValue, underValue) => {
  let field = fieldValue;
  let under = underValue;

  switch (metricId) {
    case 'temperature':
      if (under >= field) {
        under = field - 0.9;
      }
      break;
    case 'humidity':
      if (under <= field) {
        under = field + 4;
      }
      break;
    case 'wind':
      if (under >= field) {
        under = field - 1.2;
      }
      break;
    case 'solar':
      if (under >= field) {
        under = field * 0.62;
      }
      break;
    default:
      break;
  }

  return { field, under };
};

const buildDiurnalValue = (metricId, baseValue, hour, index) => {
  const tempWave = Math.sin(((hour - 9) / 24) * 2 * Math.PI);
  const daylightWave = hour >= 6 && hour <= 18
    ? Math.sin(((hour - 6) / 12) * Math.PI)
    : 0;

  if (metricId === 'temperature') {
    const value = baseValue + (3.1 * tempWave) + deterministicRipple(index, 0.35);
    return roundMetric(metricId, clamp(value, 14, 40));
  }

  if (metricId === 'humidity') {
    const inverseTempWave = -tempWave;
    const value = baseValue + (9.2 * inverseTempWave) + deterministicRipple(index, 0.8);
    return roundMetric(metricId, clamp(value, 35, 98));
  }

  if (metricId === 'wind') {
    const value = baseValue + (2.2 * daylightWave) + deterministicRipple(index, 0.45);
    return roundMetric(metricId, clamp(value, 0.6, 22));
  }

  const daytimePeak = Math.max(baseValue * 1.15, 720);
  const value = (daytimePeak * daylightWave) + (deterministicRipple(index, 18) * daylightWave);
  return roundMetric(metricId, clamp(value, 0, 1200));
};

const buildTrend = (series) => {
  if (!Array.isArray(series) || series.length < 2) {
    return { value: 0, direction: 'up' };
  }

  const current = Number(series[series.length - 1]?.value || 0);
  const previous = Number(series[series.length - 2]?.value || 0);

  if (!Number.isFinite(current) || !Number.isFinite(previous) || previous === 0) {
    return { value: 0, direction: 'up' };
  }

  const rawPercent = ((current - previous) / Math.abs(previous)) * 100;
  const value = Number(Math.abs(rawPercent).toFixed(1));

  return {
    value,
    direction: rawPercent >= 0 ? 'up' : 'down',
  };
};

const deriveByTypeValues = (farm, metricId) => {
  const stations = farm?.stations || [];
  const readingKey = METRIC_READING_KEYS[metricId];
  const fallback = METRIC_FALLBACKS[metricId];

  const fieldStations = stations.filter((station) => station.sensorType === 'campo_aperto');
  const underStations = stations.filter((station) => station.sensorType === 'retro_modulo');

  const fieldRaw = avgReading(fieldStations, readingKey);
  const underRaw = avgReading(underStations, readingKey);

  const field = Number.isFinite(fieldRaw) ? fieldRaw : fallback.field;
  const under = Number.isFinite(underRaw) ? underRaw : fallback.under;
  const normalized = normalizeByType(metricId, field, under);

  const weightedDenominator = fieldStations.length + underStations.length;
  const weightedAverage = weightedDenominator > 0
    ? ((normalized.field * fieldStations.length) + (normalized.under * underStations.length)) / weightedDenominator
    : (normalized.field + normalized.under) / 2;

  return {
    all: roundMetric(metricId, weightedAverage),
    campo_aperto: roundMetric(metricId, normalized.field),
    retro_modulo: roundMetric(metricId, normalized.under),
  };
};

export const getDashboardMetrics = (farmInput, nowInput = new Date()) => {
  const farm = farmInput || mapFarms[0] || { stations: [] };
  const now = nowInput instanceof Date ? nowInput : new Date(nowInput);

  return METRIC_DEFS.map((metricDef) => {
    const byType = deriveByTypeValues(farm, metricDef.id);
    const chartData = Array.from({ length: 24 }, (_, index) => {
      const timestamp = new Date(now.getTime() - (23 - index) * 60 * 60 * 1000);
      return {
        time: timestamp.toISOString(),
        value: buildDiurnalValue(metricDef.id, byType.all, timestamp.getHours(), index),
        hour: timestamp.getHours(),
      };
    });

    const trend = buildTrend(chartData);

    return {
      ...metricDef,
      value: chartData[chartData.length - 1]?.value ?? byType.all,
      trend,
      chartData,
      lastUpdate: now,
      byType,
    };
  });
};

// Backwards-compatible export used by legacy consumers.
export const dashboardMetrics = getDashboardMetrics(mapFarms[0], new Date());

// System status from current farm
export const getSystemStatus = (farm) => {
  const stations = farm.stations || [];
  const campoStations = stations.filter(s => s.sensorType === 'campo_aperto');
  const retroStations = stations.filter(s => s.sensorType === 'retro_modulo');

  return {
    totalStations: farm.totalStations,
    activeStations: farm.activeStations,
    inactiveStations: stations.filter(s => s.status === 'inactive').length,
    warningStations: stations.filter(s => s.status === 'warning').length,
    lastUpdate: new Date(),
    dataQuality: farm.activeStations / farm.totalStations > 0.9 ? 'excellent' : 'good',
    campoApertoCount: campoStations.length,
    retroModuloCount: retroStations.length,
    campoActive: campoStations.filter(s => s.status === 'active').length,
    retroActive: retroStations.filter(s => s.status === 'active').length
  };
};

// Get top 8 active stations from farm
export const getTopStations = (farm, typeFilter = 'all') => {
  let stations = farm.stations || [];

  // Filter by type if needed
  if (typeFilter === 'campo_aperto' || typeFilter === 'retro_modulo') {
    stations = stations.filter(s => s.sensorType === typeFilter);
  }

  // Get active stations, sorted by last transmission
  return stations
    .filter(s => s.status === 'active')
    .sort((a, b) => new Date(b.lastTransmission) - new Date(a.lastTransmission))
    .slice(0, 8)
    .map((station) => ({
      id: station.id,
      stationCode: station.stationCode,
      stationType: station.sensorType,
      status: station.status,
      lastTransmission: station.lastTransmission,
      currentReadings: station.currentReadings
    }));
};
